import type { SyncDirection } from "../components/sync-direction-icon";

export type SyncDirectionCopy = {
  done: string;
  /** No ellipsis — chip appends "…" */
  inFlight: string;
  idle: string;
  notDone: string;
  failed: string;
};

const SYNCED_FAMILY: SyncDirectionCopy = {
  done: "Synced",
  inFlight: "Syncing",
  idle: "Not yet synced",
  notDone: "Not synced",
  failed: "Sync failed",
};

const BY_DIRECTION: Record<SyncDirection, SyncDirectionCopy> = {
  bidirectional: SYNCED_FAMILY,
  unknown: SYNCED_FAMILY,
  pull_only: {
    done: "Imported",
    inFlight: "Importing",
    idle: "Not yet imported",
    notDone: "Not imported",
    failed: "Import failed",
  },
  push_only: {
    done: "Exported",
    inFlight: "Exporting",
    idle: "Not yet exported",
    notDone: "Not exported",
    failed: "Export failed",
  },
};

export function syncDirectionCopy(
  direction: SyncDirection | null | undefined,
): SyncDirectionCopy {
  if (!direction) return SYNCED_FAMILY;
  return BY_DIRECTION[direction] ?? SYNCED_FAMILY;
}

function isCreateQueued(pendingAction?: string | null): boolean {
  return pendingAction == null || pendingAction === "create_application";
}

export function candidateSyncLabel(
  state: string,
  direction: SyncDirection | null | undefined,
  pendingAction?: string | null,
): string {
  const copy = syncDirectionCopy(direction);
  if (state === "queued") {
    return isCreateQueued(pendingAction) ? copy.inFlight : "Updating";
  }
  if (state === "synced") return copy.done;
  if (state === "error") return copy.failed;
  if (state === "not_synced") {
    if (direction === "pull_only") return "Not synced";
    return copy.notDone;
  }
  if (state === "ineligible") return "Ineligible";
  return copy.done;
}

export function candidateSyncDescription(
  state: string,
  direction: SyncDirection | null | undefined,
  pendingAction?: string | null,
): string {
  if (state === "synced") {
    if (direction === "pull_only")
      return "This candidate was imported from the ATS.";
    if (direction === "push_only")
      return "This candidate was exported to the ATS.";
    return "This candidate is synced with the ATS.";
  }
  if (state === "not_synced") {
    if (direction === "push_only")
      return "This candidate hasn't been exported to the ATS yet.";
    return "This candidate hasn't been synced to the ATS yet.";
  }
  if (state === "queued") {
    if (!isCreateQueued(pendingAction))
      return "Updating this candidate in the ATS.";
    if (direction === "push_only")
      return "Exporting this candidate to the ATS.";
    return "Pushing this candidate to the ATS.";
  }
  if (state === "error") {
    if (direction === "push_only")
      return "Export failed. Try exporting this candidate again.";
    return "Sync failed. Try syncing this candidate again.";
  }
  return "This candidate can't be synced yet — some required information is missing.";
}
